/**
 * 照片切换的流体形变（docs/prototype-v0.1.md §4.5，做法和参数同原型）：同一团粒子从上一张的画面流动着变成下一张。
 * 低差异序列（R2）把粒子铺满画框；位置在两个画框之间插值（比例不同也能平滑变形）；同一 uv 上逐点交叉过渡颜色；
 * 中途叠加随进度漂移的二维流函数流场，强度 sin²(πp) 在中点最大，沿流线分四步平流，点像墨水一样卷成漩涡。
 * 两端和清晰的照片互补交接（p 在 0.11 以内和 0.89 以后此消彼长），所以首尾画面和照片本身一致。
 *
 * 面板里的照片是普通图片，所以形变画在照片上方一块单独的 WebGL 画布上；第一次切换时才建，之后复用。
 */
import * as THREE from 'three';

// 3D simplex 噪声的解析梯度（Gustavson & McEwan 的 webgl-noise 思路），原样取自原型
const NOISE_GLSL = /* glsl */ `
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute(vec4 x) { return mod289(((x * 34.0) + 10.0) * x); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
vec3 snoiseGrad(vec3 v) {
  const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
  vec3 i = floor(v + dot(v, C.yyy)), x0 = v - i + dot(i, C.xxx);
  vec3 g = step(x0.yzx, x0.xyz), l = 1.0 - g, i1 = min(g.xyz, l.zxy), i2 = max(g.xyz, l.zxy);
  vec3 x1 = x0 - i1 + C.xxx, x2 = x0 - i2 + C.yyy, x3 = x0 - 0.5;
  i = mod289(i);
  vec4 p = permute(permute(permute(i.z + vec4(0.0, i1.z, i2.z, 1.0)) + i.y + vec4(0.0, i1.y, i2.y, 1.0)) + i.x + vec4(0.0, i1.x, i2.x, 1.0));
  vec4 j = p - 49.0 * floor(p * (1.0 / 49.0));
  vec4 x_ = floor(j * (1.0 / 7.0)), y_ = floor(j - 7.0 * x_);
  vec4 x = (x_ * 2.0 + 0.5) / 7.0 - 1.0, y = (y_ * 2.0 + 0.5) / 7.0 - 1.0;
  vec4 h = 1.0 - abs(x) - abs(y);
  vec4 b0 = vec4(x.xy, y.xy), b1 = vec4(x.zw, y.zw);
  vec4 s0 = floor(b0) * 2.0 + 1.0, s1 = floor(b1) * 2.0 + 1.0, sh = -step(h, vec4(0.0));
  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy, a1 = b1.xzyw + s1.xzyw * sh.zzww;
  vec3 g0 = vec3(a0.xy, h.x), g1 = vec3(a0.zw, h.y), g2 = vec3(a1.xy, h.z), g3 = vec3(a1.zw, h.w);
  vec4 nr = taylorInvSqrt(vec4(dot(g0, g0), dot(g1, g1), dot(g2, g2), dot(g3, g3)));
  g0 *= nr.x; g1 *= nr.y; g2 *= nr.z; g3 *= nr.w;
  vec4 m = max(0.5 - vec4(dot(x0, x0), dot(x1, x1), dot(x2, x2), dot(x3, x3)), 0.0);
  vec4 m2 = m * m, m4 = m2 * m2, pd = vec4(dot(g0, x0), dot(g1, x1), dot(g2, x2), dot(g3, x3));
  vec4 t = m2 * m * pd;
  return 105.0 * (-8.0 * (t.x * x0 + t.y * x1 + t.z * x2 + t.w * x3) + m4.x * g0 + m4.y * g1 + m4.z * g2 + m4.w * g3);
}
`;

const VERT = /* glsl */ `
uniform sampler2D uA;
uniform sampler2D uB;
// 照片按 object-fit: cover 裁进画框：xy 缩放，zw 偏移
uniform vec4 uCropA;
uniform vec4 uCropB;
// 两个画框的大小和当前画框中心，单位是 CSS 像素
uniform vec2 uSizeA;
uniform vec2 uSizeB;
uniform vec2 uCenter;
uniform float uProgress;
uniform float uPhase;
uniform float uAmp;
uniform float uNoise;
uniform float uPointPx;
uniform float uLodA;
uniform float uLodB;
attribute vec2 aUV;
varying vec3 vColor;
${NOISE_GLSL}
void main() {
  float p = clamp(uProgress, 0.0, 1.0), w = p * p * (3.0 - 2.0 * p);
  float sn = sin(3.14159265 * p), env = (p > 0.0 && p < 1.0) ? sn * sn : 0.0;
  vec2 base = mix((aUV - 0.5) * uSizeA, (aUV - 0.5) * uSizeB, w);
  vec2 q = base;
  if (env > 0.0) {
    // ψ 随进度在第三维漂移，流场本身也在演化；速度 (∂ψ/∂y, −∂ψ/∂x) 在画面内无散度，不会挤出空洞
    float tau = 4.3 + uPhase + p * 1.4;
    for (int k = 0; k < 4; k++) {
      vec3 g = snoiseGrad(vec3(q * uNoise + vec2(2.7, 8.1), tau));
      q += vec2(g.y, -g.x) * (uAmp * env * 0.25);
    }
  }
  vec3 a = textureLod(uA, aUV * uCropA.xy + uCropA.zw, uLodA).rgb;
  vec3 b = textureLod(uB, aUV * uCropB.xy + uCropB.zw, uLodB).rgb;
  vColor = mix(a, b, w);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(q + uCenter, 0.0, 1.0);
  // 中段放大点径，补上被拉开的空隙
  gl_PointSize = clamp(uPointPx * mix(1.0, 1.8, env), 1.0, 20.0);
}
`;

const FRAG = /* glsl */ `
uniform float uOpacity;
varying vec3 vColor;
void main() {
  float d = length(gl_PointCoord - 0.5) * 2.0;
  if (d > 1.0) discard;
  gl_FragColor = vec4(vColor, (1.0 - smoothstep(0.6, 1.0, d)) * uOpacity);
  #include <colorspace_fragment>
}
`;

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const smooth = (t: number) => t * t * (3 - 2 * t);

export interface MorphRun {
  /** 上一张：正显示在面板里的那张图（已解码） */
  from: HTMLImageElement;
  /** 下一张（已解码） */
  to: HTMLImageElement;
  /** 画框宽度和两张各自的画框高度，CSS 像素 */
  width: number;
  fromHeight: number;
  toHeight: number;
  /** 每一对照片固定的流场相位 */
  phase: number;
  /** 每帧：当前画框高度、进度、粒子层的不透明度 */
  onFrame(height: number, p: number, particles: number): void;
  onDone(): void;
}

export class PhotoMorph {
  private renderer: THREE.WebGLRenderer | null = null;
  private failed = false;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -1, 1);
  private readonly material: THREE.ShaderMaterial;
  private readonly host: HTMLElement;
  private readonly points: number;
  private readonly reduced: boolean;
  private run: (MorphRun & { t0: number; raf: number; textures: THREE.Texture[] }) | null = null;
  private readonly duration = 1.5;

  constructor(host: HTMLElement, opts: { points: number; reduced: boolean }) {
    this.host = host;
    this.points = opts.points;
    this.reduced = opts.reduced;
    const N = this.points, uv = new Float32Array(N * 2);
    // R2 低差异序列铺满画框；每点加不到一个间距的抖动，打散规则点阵造成的摩尔纹
    const r = mulberry32(20261002), j = 0.9 / Math.sqrt(N);
    for (let i = 0; i < N; i++) {
      uv[i * 2] = THREE.MathUtils.clamp(((0.5 + i * 0.7548776662466927) % 1) + (r() - 0.5) * j, 0, 1);
      uv[i * 2 + 1] = THREE.MathUtils.clamp(((0.5 + i * 0.5698402909980532) % 1) + (r() - 0.5) * j, 0, 1);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(N * 3), 3));
    g.setAttribute('aUV', new THREE.BufferAttribute(uv, 2));
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        uA: { value: null },
        uB: { value: null },
        uCropA: { value: new THREE.Vector4(1, 1, 0, 0) },
        uCropB: { value: new THREE.Vector4(1, 1, 0, 0) },
        uSizeA: { value: new THREE.Vector2(1, 1) },
        uSizeB: { value: new THREE.Vector2(1, 1) },
        uCenter: { value: new THREE.Vector2() },
        uProgress: { value: 0 },
        uPhase: { value: 0 },
        uAmp: { value: 1 },
        uNoise: { value: 0.01 },
        uPointPx: { value: 2 },
        uLodA: { value: 0 },
        uLodB: { value: 0 },
        uOpacity: { value: 0 },
      },
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    const pts = new THREE.Points(g, this.material);
    pts.frustumCulled = false;
    this.scene.add(pts);
  }

  get running(): boolean {
    return this.run !== null;
  }

  private ensure(): THREE.WebGLRenderer | null {
    if (this.renderer || this.failed) return this.renderer;
    try {
      const r = new THREE.WebGLRenderer({ alpha: true, antialias: false, powerPreference: 'high-performance' });
      r.setClearColor(0x000000, 0);
      r.domElement.className = 'panel-morph';
      r.domElement.hidden = true;
      r.domElement.setAttribute('aria-hidden', 'true');
      this.host.append(r.domElement);
      this.renderer = r;
    } catch {
      // 拿不到 WebGL（比如上下文太多）：直接换图
      this.failed = true;
    }
    return this.renderer;
  }

  /** 照片裁进画框的方式和 object-fit: cover 一样 */
  private texture(img: HTMLImageElement, boxW: number, boxH: number, crop: THREE.Vector4): { tex: THREE.Texture; lod: number } {
    const tex = new THREE.Texture(img);
    tex.colorSpace = THREE.SRGBColorSpace;
    // 照片不透明，预乘与否结果一样；预乘上传可以绕开 Safari 不预乘上传时的坏点（见 galaxy.ts 的 canvasTexture）
    tex.premultiplyAlpha = true;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.needsUpdate = true;
    const ia = img.naturalWidth / Math.max(1, img.naturalHeight), ba = boxW / Math.max(1, boxH);
    const sx = Math.min(1, ba / ia), sy = Math.min(1, ia / ba);
    crop.set(sx, sy, (1 - sx) / 2, (1 - sy) / 2);
    // 每颗粒子覆盖几个纹素，就往上取几级 mipmap，免得闪烁
    const texelsPerPoint = Math.sqrt((sx * img.naturalWidth * sy * img.naturalHeight) / this.points);
    return { tex, lod: Math.max(0, Math.log2(texelsPerPoint) - 0.5) };
  }

  /** 开始一次形变；做不了（没有 WebGL）时返回 false，调用方直接换图 */
  start(m: MorphRun): boolean {
    this.finish();
    const r = this.ensure();
    if (!r || !m.from.naturalWidth || !m.to.naturalWidth) return false;
    const u = this.material.uniforms;
    const A = this.texture(m.from, m.width, m.fromHeight, u.uCropA!.value as THREE.Vector4);
    const B = this.texture(m.to, m.width, m.toHeight, u.uCropB!.value as THREE.Vector4);
    u.uA!.value = A.tex;
    u.uB!.value = B.tex;
    u.uLodA!.value = A.lod;
    u.uLodB!.value = B.lod;
    (u.uSizeA!.value as THREE.Vector2).set(m.width, m.fromHeight);
    (u.uSizeB!.value as THREE.Vector2).set(m.width, m.toHeight);
    u.uPhase!.value = m.phase;
    // 参考作品换算到画框高度：噪声 1.6/H（漩涡更大，像液体而不是碎褶），位移约 0.08H
    const H = (m.fromHeight + m.toHeight) / 2;
    u.uNoise!.value = 1.6 / H;
    u.uAmp!.value = H * 0.08 * (this.reduced ? 0.4 : 1);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    u.uPointPx!.value = Math.sqrt((m.width * Math.max(m.fromHeight, m.toHeight)) / this.points) * 1.9 * dpr;
    const hMax = Math.max(m.fromHeight, m.toHeight);
    r.setPixelRatio(dpr);
    r.setSize(m.width, hMax);
    Object.assign(this.camera, { left: -m.width / 2, right: m.width / 2, top: hMax / 2, bottom: -hMax / 2 });
    this.camera.updateProjectionMatrix();
    r.domElement.hidden = false;
    this.run = { ...m, t0: performance.now(), raf: 0, textures: [A.tex, B.tex] };
    this.frame();
    return true;
  }

  private frame(): void {
    const run = this.run, r = this.renderer;
    if (!run || !r) return;
    const k = Math.min(1, (performance.now() - run.t0) / 1000 / this.duration);
    const p = smooth(k), w = smooth(p);
    // 互补交接：两端清晰的照片和粒子此消彼长，中段只剩粒子
    const edge = Math.min(1, Math.min(p, 1 - p) * 9), s = smooth(edge);
    const h = run.fromHeight + (run.toHeight - run.fromHeight) * w, hMax = Math.max(run.fromHeight, run.toHeight);
    const u = this.material.uniforms;
    u.uProgress!.value = p;
    u.uOpacity!.value = s;
    // 画框顶边固定，高度跟着变
    (u.uCenter!.value as THREE.Vector2).set(0, hMax / 2 - h / 2);
    run.onFrame(h, p, s);
    r.render(this.scene, this.camera);
    if (k >= 1) this.finish();
    else run.raf = requestAnimationFrame(() => this.frame());
  }

  /** 立刻结束（换下一张、关面板时） */
  finish(): void {
    const run = this.run;
    if (!run) return;
    this.run = null;
    cancelAnimationFrame(run.raf);
    if (this.renderer) {
      this.renderer.clear();
      this.renderer.domElement.hidden = true;
    }
    const u = this.material.uniforms;
    u.uA!.value = u.uB!.value = null;
    for (const t of run.textures) t.dispose();
    run.onDone();
  }
}
